import { html, LitElement, css} from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { voyageCursorStyles } from '../../voyage-style/src/voyage-style';

import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import { PlanChangedEvent, VesselPlanningModel } from '../../../src/models/vessel-planning-model';
import { VoyagePlansModel } from '../../../src/models/voyage-plans-model';
import { TraceService, EVENT_CATEGORIES } from '../../../src/services/trace-service';
import './voyage-time-slider';
import './voyage-time-popup-control';
import './voyage-time-scale-control';
import { VoyageRoute } from '../../../src/models/voyage-routing-model';
import { SuggestedRouteEvents } from '../../voyage-suggested-routes/src/types';

interface Waypoint
{
    timestamp: number;
}

function isVessel() {
    return (<any>window)["appenv"].vesselIMONumber !== undefined;
}

@customElement("voyage-time-control")
export class VoyageTimeControl extends LitElement {

    @property({type: Boolean}) isPlaying: boolean = false;
    @property({type: Number}) playerTime: number = -1;
    @property({type: Boolean}) showSpeedSelection: boolean = false;
    @property({type: Boolean}) showScaleSelection: boolean = false;
    @property({type: Number}) playbackRate: number = 1800;
    @property({type: Number}) timescale: number = 0;
    @property({type: String}) mode: string = "";

    constructor() {
        super();
        this._imo = (<any>window)["appenv"].vesselIMONumber || 0;

        this._onPlanChanged = this._onPlanChanged.bind(this);
        this._onExecutingPlanChanged = this._onExecutingPlanChanged.bind(this);
        this._onPlansModelUpdated = this._onPlansModelUpdated.bind(this);
    }

    get planningScheduleId() {
        return this._planningScheduleId;
    }

    set planningScheduleId(value) {
        this._planningScheduleId = value;
        this._initPlans();
    }

    get imo() {
        return this._imo;
    }

    set imo(value) {
        this._imo = value;
        this._initPlans();
    }

    static get styles() {
        return [
            voyageCursorStyles,
            css`
                :host {
                    margin:0;
                    -webkit-user-select: none;
                    -moz-user-select: none;
                    user-select: none;
                }

                .container {
                    position: relative;
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                    background: var(--timeline-bg-color, black);
                    height: 100%;
                    width: 100%;
                }

                button {
                    box-sizing: border-box;
                    display: block;
                    background: var(--timeline-fg-color, white);
                    border: 1px solid var(--neutrals-grey-06, grey);
                    border-radius: 3px;
                    height: 33px;
                    width: 33px;
                    outline: none;
                    padding: 0;
                    font-family: MaerskText;
                    font-size: 14px;
                    color: var(--timeline-bg-color, black);
                }

                button:disabled {
                    opacity: 0.3;
                }

                button[selected] {
                    background: var(--timeline-button-selected-color, grey);
                }

                button > svg {
                    display: block;
                    fill: currentColor;
                    width: 16px;
                    margin: auto;
                }

                #playpause {
                    width: 41px;
                    height: 41px;
                }

                #stepBack {
                    margin-left: 12px;
                    border-radius: 3px 0 0 3px;
                }

                #stepForward {
                    border-radius: 0 3px 3px 0;
                }

                .stepButton > svg {
                    height: 14px;
                }

                #speed {
                    margin-left: 20px;
                }

                #scale {
                    margin-left: 20px;
                    margin-right: 20px;
                }

                #timeslider {
                    height: 100%;
                    flex-grow: 1;
                    border-left: 3px solid var(--timeline-divider-color, grey);
                }

                #speedSelection {
                    position: absolute;
                    bottom: 65px;
                    left: 95px;
                }

                .speedButton {
                    background: var(--neutrals-grey-10, white);
                    color: var(--neutrals-grey-02, black);
                    box-sizing: border-box;
                    border: none;
                    border-radius: 0;
                    height: 31px;
                    width: 39px;
                    overflow: hidden;
                    line-height: 10px;
                }

                .speedButton[selected] {
                    background: var(--neutrals-grey-07, grey);
                }

                .speedButton:first-child {
                    border-right: 1px solid var(--neutrals-grey-06, grey);
                }

                .speedButton:last-child {
                    border-left: 1px solid var(--neutrals-grey-06, grey);
                }

                .unitText {
                    font-size: 10px;
                }

                #scaleSelection {
                    position: absolute;
                    bottom: 65px;
                    left: 165px;
                }
        `];
    }

    render() {
        this._updateWaypointIndeces();

        return html`
        <div class="container">
            <button id="stepBack" class="stepButton" ?disabled=${this._prevWaypointIndex === -1} @click=${this._stepBack}>
                ${SvgIcons.stepBackward}
            </button>
            <button class="playcontrol" id="playpause" @click=${this._togglePlay}>
                ${this.isPlaying ? SvgIcons.pause : SvgIcons.play}
            </button>
            <button id="stepForward" class="stepButton" ?disabled=${this._nextWaypointIndex === -1} @click=${this._stepForward}>
                ${SvgIcons.stepForward}
            </button>

            <button id="speed" ?selected=${this.showSpeedSelection} @click=${this._toggleSpeedSelector}>
                ${this.playbackRate === 1800 ? SvgIcons.lightTachometerSlow :
                                               this.playbackRate === 3600 ? SvgIcons.lightTachometerAverage : SvgIcons.lightTachometerFast}
            </button>

            <button id="scale" ?selected=${this.showScaleSelection} @click=${this._toggleScaleSelector}>
                ${SvgIcons.lightArrowsH}
            </button>

            <voyage-time-slider id="timeslider" .mode=${this.mode} .planningScheduleId=${this.planningScheduleId} .scale=${this.timescale} .focusTime=${this.playerTime}></voyage-time-slider>

            ${this.showSpeedSelection ? html`
            <voyage-time-popup-control id="speedSelection" ?darkarrow=${this.playbackRate === 3600}>
                <button ?selected=${this.playbackRate === 1800} class="speedButton" @click=${() => this._speedClicked(1800)}>
                    30 <span class="unitText">min/s</span>
                </button>
                <button ?selected=${this.playbackRate === 3600} class="speedButton" @click=${() => this._speedClicked(3600)}>
                    60 <span class="unitText">min/s</span>
                </button>
                <button ?selected=${this.playbackRate === 14400} class="speedButton" @click=${() => this._speedClicked(14400)}>
                    240 <span class="unitText">min/s</span>
                </button>
            </voyage-time-popup-control>` : ""}

            ${this.showScaleSelection ? html`
            <voyage-time-popup-control id="scaleSelection">
                <voyage-time-scale-control .selection=${this.timescale}
                                           @selection-changed=${ (evt: CustomEvent) => this.timescale = evt.detail }>
                </voyage-time-scale-control>
            </voyage-time-popup-control>` : ""}
        </div>
        `;
    }

    connectedCallback() {
        super.connectedCallback();

        if (isVessel()) {
            VesselPlanningModel!.addEventListener(SuggestedRouteEvents.PlanChanged, <EventListener>this._onPlanChanged);
            VesselPlanningModel!.addEventListener("executing-plan-changed", this._onExecutingPlanChanged);
        } else {
            VoyagePlansModel!.addEventListener('plans-updated', <EventListener>this._onPlansModelUpdated);
        }

        this.addEventListener('api-call-tracker-initialized', () => {
            localStorage.setItem('play-clicked', "0");
        });

        this._initPlans();
    }

    disconnectedCallback() {
        super.disconnectedCallback();

        if (isVessel()) {
            VesselPlanningModel!.removeEventListener(SuggestedRouteEvents.PlanChanged, <EventListener>this._onPlanChanged);
            VesselPlanningModel!.removeEventListener("executing-plan-changed", this._onExecutingPlanChanged);
        } else {
            VoyagePlansModel!.removeEventListener('plans-updated', <EventListener>this._onPlansModelUpdated);
        }

        if (this._updateWPIndecesTimer) {
            clearTimeout(this._updateWPIndecesTimer);
        }

        this.removeEventListener('api-call-tracker-initialized', () => {
            localStorage.setItem('play-clicked', '0');
        });
    }

    @property({ attribute: false }) private _nextWaypointIndex: number = -1;
    @property({ attribute: false }) private _prevWaypointIndex: number = -1;
    @property({ attribute: false }) private _planningScheduleId: string = "";
    private _imo: number;
    private _waypoints: Waypoint[] = [];
    private _updateWPIndecesTimer: number = 0;

    private _togglePlay() {
        this.isPlaying = !this.isPlaying;
        TraceService!.event(EVENT_CATEGORIES.TIME_CONTROL, 'click', 'Play/Pause toggled');

        // Fast forward to first waypoint if we have one (and it is not in the past)
        if (this.isPlaying && this._nextWaypointIndex === 0)
        {
            this.dispatchEvent(new CustomEvent('focustime-update', {
                detail: { focustime: this._waypoints[0].timestamp },
                bubbles: true,
                composed: true
            }));
        }

        if(this.isPlaying){
            const count = parseInt(localStorage.getItem('play-clicked') || '0') + 1;
            localStorage.setItem('play-clicked', ""+count);
        }

        this.dispatchEvent(new CustomEvent('toggle-play', {detail: { play: this.isPlaying }}));
    }

    private _stepBack() {
        TraceService!.event(EVENT_CATEGORIES.TIME_CONTROL, 'click', 'Step backward');

        this.dispatchEvent(new CustomEvent('focustime-update', {
            detail: { focustime: this._waypoints[this._prevWaypointIndex].timestamp },
            bubbles: true,
            composed: true
        }));
    }

    private _stepForward() {
        TraceService!.event(EVENT_CATEGORIES.TIME_CONTROL, 'click', 'Step forward');

        this.dispatchEvent(new CustomEvent('focustime-update', {
            detail: { focustime: this._waypoints[this._nextWaypointIndex].timestamp },
            bubbles: true,
            composed: true
        }));
    }

    private _toggleSpeedSelector() {
        TraceService!.event(EVENT_CATEGORIES.TIME_CONTROL, 'click', 'Speed selector toggled');

        this.showScaleSelection = false;
        this.showSpeedSelection = !this.showSpeedSelection;
    }

    private _toggleScaleSelector() {
        TraceService!.event(EVENT_CATEGORIES.TIME_CONTROL, 'click', 'Scale selector toggled');

        this.showSpeedSelection = false;
        this.showScaleSelection = !this.showScaleSelection;
    }

    private _speedClicked(speed: number) {
        this.playbackRate = speed;
        this.dispatchEvent(new CustomEvent('playbackrate-update', { detail: { playbackrate: speed } }));
        this.showSpeedSelection = false;

        TraceService!.event(EVENT_CATEGORIES.TIME_CONTROL, 'change', 'Playback speed changed', speed);
    }

    private async _initPlans() {
        if (isVessel()) {
            const executingPlan = await VesselPlanningModel!.getExecutingPlan();
            let planningPlan = null;

            if (this._planningScheduleId) {
                const plan = await VesselPlanningModel!.getCurrentPlan(this._planningScheduleId);
                if (plan && plan.selectedRouteIndex >= 0 && plan.routes[plan.selectedRouteIndex]) {
                    planningPlan = plan;
                }
            }

            this._waypoints = [];
            if (executingPlan) {
                this._waypoints = this._getWaypoints(executingPlan.routes[executingPlan.selectedRouteIndex], executingPlan.etd);
            }
            if (planningPlan) {
                this._waypoints = this._waypoints.concat(this._getWaypoints(planningPlan.routes[planningPlan.selectedRouteIndex], planningPlan.etd));
            }

        } else {
            // Shore
            const plan = VoyagePlansModel!.choosePlan(this._imo);
            if (plan && plan.routes.length > 0) {
                this._waypoints = this._getWaypoints(plan.routes[plan.selectedRouteIndex], plan.etd)
            } else {
                this._waypoints = [];
            }
        }

        this._updateWaypointIndeces();
    }

    private _onPlanChanged(evt: PlanChangedEvent) {
        if (evt.detail.scheduleId === this._planningScheduleId) {
            this._initPlans();
        }
    }

    private _onExecutingPlanChanged() {
        this._initPlans();
    }

    private _onPlansModelUpdated(evt: CustomEvent) {
        if (evt.detail.imoNo === this._imo) {
            this._initPlans();
        }
    }

    private _getWaypoints(route: VoyageRoute, planEtd: number) {
        return route.route_geometry.filter( feature => feature.geometry.type === "Point" ).map( point => {

            // Note: First waypoint in a route does not have a timestamp, use planEtd in this case
            return {
                timestamp: new Date(point.properties.timestamp || planEtd).getTime()
            }
        });
    }

    private _updateWaypointIndeces() {
        if (this._waypoints.length === 0) {
            this._nextWaypointIndex = -1;
            this._prevWaypointIndex = -1;
            if (this._updateWPIndecesTimer) {
                clearTimeout(this._updateWPIndecesTimer);
                this._updateWPIndecesTimer = 0;
            }
            return;
        }

        const currentTime = Math.max(this.playerTime, Date.now());

        // Start search at current previous waypoint (if any)
        let startIdx = Math.max(0, this._prevWaypointIndex);
        if (startIdx > 0 && currentTime < this._waypoints[startIdx].timestamp) {
            // Time was set backwards - start from index 0
            startIdx = 0;
        }

        // Find first waypoint past currentTime
        let i = startIdx;
        while (i < this._waypoints.length && this._waypoints[i].timestamp <= currentTime) {
            i++;
        }
        this._nextWaypointIndex = i === this._waypoints.length ? -1 : i;
        this._prevWaypointIndex = i === 0 ? -1 : this._waypoints[i-1].timestamp === currentTime ? i-2 : i-1;

        // Don't allow going backwards in time
        if (this._prevWaypointIndex !== -1 && this._waypoints[this._prevWaypointIndex].timestamp < Date.now()) {
            this._prevWaypointIndex = -1;
        }
        if (this._nextWaypointIndex !== -1 && this._waypoints[this._nextWaypointIndex].timestamp < Date.now()) {
            this._nextWaypointIndex = -1;
        }

        // Keep indeces updated as time passes
        if (this._updateWPIndecesTimer) {
            clearTimeout(this._updateWPIndecesTimer);
        }
        this._updateWPIndecesTimer = window.setTimeout(() => this._updateWaypointIndeces(), 1000);
    }
}
